import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { Activity, ArrowRight, Building2, Clock, HandCoins, Landmark, LayoutDashboard, PiggyBank, Users, Wallet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { EmptyState, PageHeader, StatCard, StatusPill } from "@/components/common";
import { TxTable } from "@/components/TxTable";
import { Button } from "@/components/ui/button";
import { PetugasHome } from "@/components/PetugasHome";
import { usePoll } from "@/lib/live";
import { ResortLinks } from "@/pages/LaporanResortPage";
import { LOAN_STATUS, ROLE_LABELS, rupiah, rupiahShort, TX_LABELS } from "@/lib/format";
import { MetricsBoard } from "@/components/MetricsBoard";
import { SuperAdminPanels } from "@/components/SuperAdminPanels";
import { DueReminder } from "@/components/DueReminder";

const tooltipStyle = { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 };
const PIE_COLORS = { diajukan: "#f59e0b", disetujui: "#0ea5e9", ditolak: "#f43f5e", berjalan: "hsl(var(--primary))", lunas: "#94a3b8" };

const Panel = ({ title, children, className = "", testId }) => (
  <div className={`panel ${className}`} data-testid={testId}>
    <p className="font-heading font-semibold mb-4">{title}</p>
    {children}
  </div>
);

const TrendChart = ({ series }) => (
  <ResponsiveContainer width="100%" height={260}>
    <AreaChart data={series.map((d) => ({ ...d, label: d.date.slice(5) }))}>
      <defs>
        <linearGradient id="gSetor" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} /><stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
      <YAxis tickFormatter={rupiahShort} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={70} />
      <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [rupiah(v), TX_LABELS[n]]} />
      <Area type="monotone" dataKey="setor" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#gSetor)" />
      <Area type="monotone" dataKey="angsuran" stroke="#0ea5e9" strokeWidth={2} fill="transparent" />
      <Area type="monotone" dataKey="tarik" stroke="#f43f5e" strokeWidth={1.5} fill="transparent" strokeDasharray="4 3" />
    </AreaChart>
  </ResponsiveContainer>
);

const TodayBars = ({ today }) => (
  <ResponsiveContainer width="100%" height={220}>
    <BarChart data={Object.keys(TX_LABELS).map((k) => ({ name: TX_LABELS[k], total: today[k].total }))}>
      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
      <YAxis tickFormatter={rupiahShort} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={64} />
      <Tooltip contentStyle={tooltipStyle} formatter={(v) => rupiah(v)} cursor={{ fill: "hsl(var(--muted))" }} />
      <Bar dataKey="total" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

const LoanPie = ({ data }) => {
  const rows = Object.entries(data).map(([k, v]) => ({ name: LOAN_STATUS[k]?.label || k, key: k, value: v }));
  if (!rows.length) return <p className="text-sm text-muted-foreground py-16 text-center">Belum ada pinjaman.</p>;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie data={rows} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
          {rows.map((r) => <Cell key={r.key} fill={PIE_COLORS[r.key]} />)}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} />
        <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

const CabangDashboard = ({ user }) => {
  const [d, setD] = useState(null);
  const load = () => api.get("/dashboard/summary").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  usePoll(load);
  if (!d) return <p className="text-muted-foreground">Memuat ringkasan...</p>;
  const todayIn = d.today.setor.total + d.today.angsuran.total;
  return (
    <div className="space-y-6">
      {user.role === "petugas" && (
        <Panel title="Resort Saya" testId="dashboard-my-resorts">
          {d.resorts?.length ? <ResortLinks resorts={d.resorts} testId="dashboard-resort-links" /> : <p className="text-sm text-muted-foreground">Belum ada resort yang ditugaskan.</p>}
        </Panel>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard label="Total Simpanan" value={rupiah(d.total_simpanan)} hint={`${d.anggota_aktif} anggota aktif`} icon={PiggyBank} testId="stat-total-simpanan" />
        <StatCard label="Pinjaman Berjalan" value={rupiah(d.pinjaman_berjalan_sisa)} hint={`${d.pinjaman_berjalan_count} pinjaman · sisa tagihan`} icon={HandCoins} testId="stat-pinjaman-berjalan" />
        <StatCard label="Transaksi Hari Ini" value={d.today_count} hint={`Masuk ${rupiah(todayIn)}`} icon={Activity} testId="stat-transaksi-hari-ini" />
        <StatCard label="Menunggu Proses" value={d.pinjaman_menunggu} hint="Pinjaman diajukan / disetujui" icon={Clock} testId="stat-pinjaman-menunggu" tone="muted" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="Arus Transaksi 14 Hari" className="lg:col-span-2" testId="chart-trend"><TrendChart series={d.series} /></Panel>
        <Panel title="Status Pinjaman" testId="chart-loan-status"><LoanPie data={d.loan_status} /></Panel>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="Nominal Hari Ini" testId="chart-today"><TodayBars today={d.today} /></Panel>
        <Panel title="Transaksi Terbaru" className="lg:col-span-2" testId="recent-transactions">
          <TxTable rows={d.recent} testId="recent-tx" />
          <Link to="/riwayat" className="inline-flex items-center text-sm font-medium text-primary mt-4" data-testid="dashboard-see-history">Lihat semua riwayat <ArrowRight className="h-4 w-4 ml-1" /></Link>
        </Panel>
      </div>
    </div>
  );
};

const OverviewTable = ({ rows, unit, testId }) => {
  const isWil = unit === "wilayah";
  const canReport = true;
  return (
    <div className="overflow-x-auto -mx-2 px-2" data-testid={testId}>
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b">
            <th className="py-2.5 pr-4 font-medium">{isWil ? "Kantor Wilayah" : "Kantor Cabang"}</th>
            {isWil && <th className="py-2.5 px-4 font-medium text-right">Cabang</th>}
            <th className="py-2.5 px-4 font-medium text-right">Anggota</th>
            <th className="py-2.5 px-4 font-medium text-right">Simpanan</th>
            <th className="py-2.5 px-4 font-medium text-right">Pokok Pinjaman</th>
            <th className="py-2.5 px-4 font-medium text-right">Sisa Tagihan</th>
            <th className="py-2.5 pl-4 font-medium text-right">Status</th>
            {canReport && <th className="py-2.5 pl-4 font-medium text-right">Laporan</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40 transition-colors" data-testid={`${testId}-row-${r.id}`}>
              <td className="py-3 pr-4 font-medium">
                <span className="inline-flex items-center gap-2">
                  <span className="h-8 w-8 rounded-lg bg-primary/12 text-primary grid place-items-center shrink-0">
                    {isWil ? <Landmark className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
                  </span>
                  {r.name}
                </span>
              </td>
              {isWil && <td className="py-3 px-4 text-right font-mono">{r.cabang_count}</td>}
              <td className="py-3 px-4 text-right font-mono">{r.anggota}</td>
              <td className="py-3 px-4 text-right font-mono">{rupiah(r.total_simpanan)}</td>
              <td className="py-3 px-4 text-right font-mono">{rupiah(r.pinjaman_pokok)}</td>
              <td className="py-3 px-4 text-right font-mono text-primary">{rupiah(r.pinjaman_sisa)}</td>
              <td className="py-3 pl-4 text-right"><StatusPill active={r.active} testId={`${testId}-status-${r.id}`} /></td>
              {canReport && <td className="py-3 pl-4 text-right"><Button asChild size="sm" variant="outline" className="h-8 rounded-full"><Link to={`/laporan-kantor/${unit}/${r.id}`} data-testid={`${testId}-report-${r.id}`}>Lihat Laporan</Link></Button></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const OverviewDashboard = ({ unit }) => {
  const isWil = unit === "wilayah";
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/overview/${unit}`).then((r) => setD(r.data)).catch(() => setD(null)); }, [unit]);
  if (!d) return <p className="text-muted-foreground">Memuat ringkasan...</p>;
  const t = d.totals;
  const empty = d.rows.length === 0;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard label="Total Simpanan" value={rupiah(t.total_simpanan)} hint={`${t.anggota_aktif} anggota aktif`} icon={PiggyBank} testId="ov-total-simpanan" />
        <StatCard label="Pokok Pinjaman Berjalan" value={rupiah(t.pinjaman_pokok)} hint={`${t.pinjaman_berjalan} pinjaman berjalan`} icon={HandCoins} testId="ov-pinjaman-pokok" />
        <StatCard label="Sisa Tagihan" value={rupiah(t.pinjaman_sisa)} hint="Belum tertagih" icon={Wallet} testId="ov-pinjaman-sisa" />
        <StatCard label={isWil ? "Kantor Wilayah" : "Kantor Cabang"} value={isWil ? t.wilayah : t.cabang}
          hint={isWil ? `${t.cabang} kantor cabang` : `${t.anggota} total anggota`} icon={isWil ? Landmark : Building2} testId="ov-office-count" tone="muted" />
      </div>
      <div className="panel" data-testid="ov-breakdown-panel">
        <p className="font-heading font-semibold mb-4">{isWil ? "Rincian per Kantor Wilayah" : "Rincian per Kantor Cabang"}</p>
        {empty ? (
          <p className="text-sm text-muted-foreground py-10 text-center" data-testid="ov-breakdown-empty">
            {isWil ? "Belum ada Kantor Wilayah. Tambahkan lewat menu Worksheet." : "Belum ada Kantor Cabang. Tambahkan lewat menu Worksheet."}
          </p>
        ) : <OverviewTable rows={d.rows} unit={unit} testId="ov-breakdown-table" />}
      </div>
    </div>
  );
};

export default function DashboardPage() {
  const { user } = useAuth();
  const isCabang = Boolean(user.cabang_id);
  if (user.role === "petugas") return <PetugasHome name={user.name} />;
  const greet = new Date().getHours() < 11 ? "Selamat pagi" : new Date().getHours() < 15 ? "Selamat siang" : new Date().getHours() < 19 ? "Selamat sore" : "Selamat malam";
  return (
    <div>
      <PageHeader eyebrow={ROLE_LABELS[user.role]} title={`${greet}, ${user.name.split(" ")[0]}`}
        subtitle={isCabang ? `Ringkasan Kantor Cabang ${user.cabang_name}` : user.wilayah_name ? `Kantor Wilayah ${user.wilayah_name}` : "Panel kendali kantor pusat"} />
      {user.role === "admin_wilayah" && <DueReminder />}
      {(user.role === "admin_wilayah" || user.role === "admin_cabang") && <MetricsBoard title="Ringkasan Total" editPath="/atur-dashboard" />}
      {user.role === "super_admin" ? <SuperAdminPanels />
        : isCabang ? <CabangDashboard user={user} />
        : user.role === "viewer" ? <OverviewDashboard unit="wilayah" />
        : user.role === "admin_wilayah" ? <OverviewDashboard unit="cabang" />
        : <EmptyState icon={LayoutDashboard} title="Dashboard segera hadir" testId="dashboard-empty" text="Menu belum tersedia untuk peran ini." />}
    </div>
  );
}
